import React from 'react'
import { BusyButtonLink } from '@ztechapps/webui.button'
import type { BusyButtonProps } from '@ztechapps/webui.button'
import type { OrderPaymentStatusEnum, OrderStatusEnum } from '@ztechapps/common.contract'
import { useOrderSettle } from './useOrderSettle'

export type OrderMarkSettledButtonProps = {
  orderId: string
  balance?: number
  paymentTerms?: string
  paymentStatus?: OrderPaymentStatusEnum
  status?: OrderStatusEnum
  // Refresh the affected order/list after the settlement is recorded.
  onSuccess?: () => void | Promise<void>
  size?: BusyButtonProps['bsSize']
  className?: string
}

/**
 * Reusable "Settled" action that records an on-account settlement, usable across the
 * order detail action area and the company accounting statement rows without any
 * context dependency.
 *
 * Self-gating via `useOrderSettle`: renders nothing unless `canSettle` is true — a
 * committed order on an on-account term with an open balance that is not already
 * written off (Not Receivable). When eligible, the button opens the "Pay on account"
 * confirm dialog and records the settlement.
 */
export const OrderMarkSettledButton = ({
  orderId,
  balance,
  paymentTerms,
  paymentStatus,
  status,
  onSuccess,
  size,
  className,
}: OrderMarkSettledButtonProps) => {
  const { canSettle, onSettle, loading } = useOrderSettle({
    orderId,
    balance,
    paymentTerms,
    paymentStatus,
    status,
    onSuccess,
  })

  if (!canSettle) {
    return null
  }

  return (
    <BusyButtonLink
      bsSize={size}
      className={className}
      icon="circle-check"
      text="Settled"
      title="Record an on-account settlement and remove this order from accounts receivable"
      isBusy={loading}
      onClick={onSettle}
    />
  )
}
